import {
  CONTENT_TAG_INDICATOR_GRADIENT,
  CONTENT_TYPE_COLORS,
  CONTENT_TYPE_TEXT_COLORS,
  COURSE_TAG,
  CTF_LAB_TAG,
  CTF_TAG,
  HAS_SKILL_CHECK_LAB,
  IFRAME_TAG,
  LAB_EXPERIENCE_LAB,
  LAB_TAG,
  LEARNING_PATH_TAG,
  LINK_TAG,
  SKILL_DIVE_COLLECTION_TAG,
  SKILL_DIVE_LAB,
  VIDEO_TAG,
} from 'constants/playlists';

import ContentTypeIcon, { hasContentTypeIcon } from './ContentTypeIcon';
import Icon from 'components/icons';
import { SessionContext } from 'contexts/session';
import { colors } from 'constants/styles/colors';
import styled from 'styled-components';
import { useContext } from 'react';

export type ContentTagVariant = 'iconText' | 'text' | 'icon';

export interface IContentTypeTagContent {
  content_type?: string;
  is_lab_experience?: boolean;
  is_trackable?: boolean;
  has_skill_check_labs?: boolean;
}

/** Kept for PreviewLab and SkillDiveLabInfo, which reuse only its typography. */
export const Tag = styled.span<{ value: string }>`
  font-feature-settings: 'clig' off, 'liga' off;

  font-size: 10px;
  font-style: normal;
  font-weight: 450;
  line-height: 12px;
  text-transform: capitalize;

  span {
    padding: 4px;
  }

  .tag-label {
    color: var(--black-darkest);
    background-color: ${({ value }) => `${CONTENT_TYPE_COLORS[value]}`};
  }
  .tag-ctf {
    background-color: ${colors.dataVisualization.orange.dark};
    color: white;
  }
  .tag-hsc {
    background-color: ${colors.dataVisualization.red.lighter};
    color: #1a2535;
  }
`;

const INDICATOR_ICON_SIZE = { width: '13px', height: '13px' };

const ContentTag = styled.span<{ value: string; $variant: ContentTagVariant; $indicator: boolean }>`
  display: inline-flex;
  align-items: center;
  position: relative;
  border-radius: 3px;
  background-color: ${({ value }) =>
    CONTENT_TYPE_COLORS[value] ?? colors.dataVisualization.gray.dark};
  ${({ $variant }) => ($variant === 'iconText' ? 'gap: 3px;' : '')}

  ${({ $variant, $indicator }) => {
    if ($variant === 'icon') return `padding: 4px;${$indicator ? ' gap: 3px;' : ''}`;
    if ($indicator) return 'height: 20px; overflow: hidden; padding: 4px 0 4px 6px;';
    return 'padding: 4px 6px;';
  }}

  .tag-label {
    font-family: var(--font-family-base);
    font-size: 10px;
    font-weight: 700;
    line-height: 12px;
    white-space: nowrap;
    text-transform: capitalize;
    color: ${({ value }) => CONTENT_TYPE_TEXT_COLORS[value] ?? 'var(--black-darkest)'};
  }

  .tag-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 5px;
    background: ${CONTENT_TAG_INDICATOR_GRADIENT};
  }

  .tag-indicator-corner {
    position: absolute;
    top: -4px;
    left: 13px;
    display: inline-flex;
  }
`;

// Courses carry skill-check state directly; the learning paths and playlists
// containing them do not, so fall back to inspecting nested children.
export interface ISkillCheckSource {
  name?: string;
  content_type?: string;
  has_skill_check_labs?: boolean;
  skill_check_labs?: { name?: string }[];
  content?: { name?: string; content_type?: string; content?: ISkillCheckSource[] }[];
}

export const hasSkillCheckContent = (data?: ISkillCheckSource): boolean =>
  Boolean(data?.has_skill_check_labs) ||
  Boolean(data?.skill_check_labs?.length) ||
  Boolean(
    data?.content?.some((group) =>
      group?.content?.some(
        (item) =>
          item?.has_skill_check_labs ||
          item?.skill_check_labs?.length ||
          item?.content_type === 'skill-check'
      )
    )
  );

const usesCtfIndicator = (contentType?: string): boolean =>
  contentType === 'lab' || contentType === 'ctf-lab' || contentType === 'collection';

export const getContentTypeLabel = (
  content: IContentTypeTagContent,
  preferLabExperienceNaming = false
): string => {
  const { content_type, is_lab_experience } = content;

  switch (content_type) {
    case 'learning-path':
    case 'learning-path-with-cert':
      return LEARNING_PATH_TAG;
    case 'collection':
      return SKILL_DIVE_COLLECTION_TAG;
    case 'course':
      return COURSE_TAG;
    case 'video':
      return VIDEO_TAG;
    case 'link':
      return LINK_TAG;
    case 'iframe':
    case 'slides':
      return IFRAME_TAG;
    case 'ctf-lab':
      return CTF_LAB_TAG;
    case 'skill-check':
      return HAS_SKILL_CHECK_LAB;
    case 'lab':
      if (is_lab_experience) {
        return preferLabExperienceNaming ? LAB_EXPERIENCE_LAB : SKILL_DIVE_LAB;
      }
      return LAB_TAG;
    default:
      return content_type ?? '';
  }
};

export const useContentTypeLabel = (): ((content: IContentTypeTagContent) => string) => {
  const { hasSkillDive, isLabExperience, isEnterprisePlus } = useContext(SessionContext);
  const preferLabExperienceNaming = (isLabExperience || isEnterprisePlus) && !hasSkillDive;

  return (content: IContentTypeTagContent) =>
    getContentTypeLabel(content, preferLabExperienceNaming);
};

const ContentTypeTag = ({
  value,
  variant = 'iconText',
  is_lab_experience,
  is_trackable,
  has_skill_check_labs,
}: {
  value: string;
  variant?: ContentTagVariant;
  is_lab_experience?: boolean;
  is_trackable?: boolean;
  has_skill_check_labs?: boolean;
}): JSX.Element => {
  const getLabel = useContentTypeLabel();
  const label = getLabel({ content_type: value, is_lab_experience });

  const isCtf = usesCtfIndicator(value);
  const showIndicator = isCtf ? !!is_trackable : !!has_skill_check_labs;
  const indicatorLabel = isCtf ? CTF_TAG : HAS_SKILL_CHECK_LAB;

  // A glyph-less type would render an empty chip, so it falls back to its label.
  const resolvedVariant = variant === 'icon' && !hasContentTypeIcon(value) ? 'text' : variant;
  const showIcon = resolvedVariant === 'icon' || resolvedVariant === 'iconText';

  return (
    <ContentTag
      value={value}
      $variant={resolvedVariant}
      $indicator={showIndicator}
      title={resolvedVariant === 'icon' ? label : undefined}
      aria-label={resolvedVariant === 'icon' ? label : undefined}
    >
      {showIcon && <ContentTypeIcon value={value} />}
      {resolvedVariant !== 'icon' && <span className="tag-label">{label}</span>}

      {showIndicator &&
        (resolvedVariant === 'icon' ? (
          <span className="tag-indicator-corner" aria-label={indicatorLabel}>
            <Icon
              icon={isCtf ? 'ContentTagFlagBadge' : 'ContentTagSkillCheckBadge'}
              style={INDICATOR_ICON_SIZE}
              aria-hidden
              focusable={false}
            />
          </span>
        ) : (
          <span className="tag-indicator" aria-label={indicatorLabel}>
            <Icon
              icon={isCtf ? 'ContentTagFlag' : 'ContentTagSkillCheck'}
              style={INDICATOR_ICON_SIZE}
              aria-hidden
              focusable={false}
            />
          </span>
        ))}
    </ContentTag>
  );
};

export default ContentTypeTag;
